import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useHorizontalOverflow } from '@/hooks/useHorizontalOverflow';

import styles from './DataTable.module.scss';

interface DataTableScrollProps {
    className: string;
    /** The table's own name, which the scrolling box takes when it becomes a region. */
    label?: string;
    children: ReactNode;
}

/**
 * The box a wide table scrolls sideways in. A table with nothing focusable in it would leave
 * a keyboard user no way to reach the columns past the edge, so while the table overflows the
 * box takes focus itself (arrow keys then scroll it) and is named as a region. A table that
 * fits gets no extra tab stop.
 */
export default function DataTableScroll(props: DataTableScrollProps) {
    const { className, label, children } = props;
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const { overflows } = useHorizontalOverflow(ref);
    const regionProps = overflows
        ? {
              tabIndex: 0,
              role: 'region',
              'aria-label': label ?? t('organisms.dataTable.scrollRegion'),
          }
        : {};

    return (
        <div ref={ref} className={classNames(className, styles.scrollRegion)} {...regionProps}>
            {children}
        </div>
    );
}
